import { GoogleDriveResource } from "~/classes/models/education-resources/education-resource.model";

declare const google: any;

type googlePickerParams = {
  returnFile?: boolean;
  supportedMimeTypes?: (
    | "pdf"
    | "documents"
    | "images"
    | "sheets"
    | "drawings"
    | "slides"
  )[];
  exportMimeType?: string;
  onFilePicked?: (file: any, accessToken: string) => void;
  onFilesPicked?: (files: any[], accessToken: string) => void;
  onAccessTokenUpdated?: (accessToken: string) => void;
  folderSelect?: boolean;
  allowMultiple?: boolean;
};

const supportedMimeTypesMap = {
  images: "image/png,image/jpeg,image/jpg",
  pdf: "application/pdf",
  sheets: "application/vnd.google-apps.spreadsheet",
  slides: "application/vnd.google-apps.presentation",
  drawings: "application/vnd.google-apps.drawing",
  documents:
    "application/vnd.google-apps.document, application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

export const usePickGoogleDriveFile = ({
  supportedMimeTypes = ["documents"],
  returnFile = false,
  onFilePicked,
  onFilesPicked,
  exportMimeType,
  onAccessTokenUpdated,
  folderSelect = false,
  allowMultiple = false,
}: googlePickerParams) => {
  const accessToken = ref<any | undefined>(undefined);

  const mimeTypes = supportedMimeTypes
    .map((type) => supportedMimeTypesMap[type])
    .join(",");

  const openGooglePicker = (token: any) => {
    accessToken.value = token;

    const pickerBuilder = new google.picker.PickerBuilder()
      .setOAuthToken(token)
      .setDeveloperKey(useRuntimeConfig().public.googleApiKey)
      .setCallback(pickerCallback)
      .setAppId(useRuntimeConfig().public.googleClientId);

    if (allowMultiple) {
      pickerBuilder.enableFeature(google.picker.Feature.MULTISELECT_ENABLED);
    }

    if (folderSelect) {
      pickerBuilder.addView(
        new google.picker.DocsView()
          .setIncludeFolders(true)
          .setMimeTypes("application/vnd.google-apps.folder")
          .setSelectFolderEnabled(true)
      );
    } else {
      pickerBuilder.addView(
        new google.picker.DocsView()
          .setIncludeFolders(true)
          .setMimeTypes(mimeTypes)
          .setSelectFolderEnabled(false)
      );
    }

    const picker = pickerBuilder.build();
    picker.setVisible(true);
  };

  const pickerCallback = async (data: any) => {
    if (data[google.picker.Response.ACTION] === google.picker.Action.PICKED) {
      const documents = data[google.picker.Response.DOCUMENTS];
      const googleDoc = documents[0];

      if (returnFile != true) {
        if (onFilePicked) {
          onFilePicked(
            new GoogleDriveResource({
              driveFileId: googleDoc[google.picker.Document.ID],
              label: googleDoc[google.picker.Document.NAME],
              mimeType: googleDoc[google.picker.Document.MIME_TYPE],
            }),
            accessToken.value
          );
        }
        if (onFilesPicked) {
          const resources = documents.map(
            (doc: any) =>
              new GoogleDriveResource({
                driveFileId: doc[google.picker.Document.ID],
                label: doc[google.picker.Document.NAME],
                mimeType: doc[google.picker.Document.MIME_TYPE],
              })
          );
          onFilesPicked(resources, accessToken.value);
        }
        return;
      }

      let files = [] as CustomFile[];

      for (const document of documents) {
        const fileId = googleDoc[google.picker.Document.ID];
        const fileName = googleDoc[google.picker.Document.NAME];
        const mimeType = googleDoc[google.picker.Document.MIME_TYPE];

        // Check if the selected document is a Google Doc
        if (mimeType === "application/vnd.google-apps.document") {
          // Specify the export MIME type for a Google Doc (e.g., Microsoft Word)
          const eMimeType =
            exportMimeType ??
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
          const exportUrl = `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=${encodeURIComponent(
            eMimeType
          )}`;

          try {
            const response = await fetch(exportUrl, {
              headers: new Headers({
                Authorization: `Bearer ${accessToken.value}`,
              }),
            });
            if (!response.ok) throw new Error("Failed to download file");
            const blob = await response.blob();
            // Convert blob to Uint8Array
            const arrayBuffer = await blob.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);

            const customFile = {
              data: uint8Array,
              name: `${fileName}.docx`,
              size: blob.size,
              type: exportMimeType,
            } as CustomFile;

            if (onFilePicked) {
              onFilePicked(customFile, accessToken.value);
            }

            files.push(customFile);
          } catch (error) {
            console.error("Error downloading or uploading file:", error);
          }
        } else {
          // For non-Google Docs, you would need to implement a similar download and upload logic,
          // considering the direct file download URL from Google Drive API

          // For non-Google Docs, directly download the file
          const fileId = googleDoc.id; // The ID of the file to download

          try {
            // Note: This URL and method of downloading files works for small to medium-sized files.
            // For larger files, you should use the "files.get" method with the parameter "alt=media"
            // and handle the stream appropriately.
            const response = await fetch(
              `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
              {
                headers: new Headers({
                  Authorization: "Bearer " + accessToken.value,
                }),
              }
            );

            if (!response.ok) {
              throw new Error(
                `Server responded with ${response.status}: ${response.statusText}`
              );
            }

            const blob = await response.blob();
            // Convert blob to Uint8Array
            const arrayBuffer = await blob.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);

            const customFile = {
              data: uint8Array,
              name: fileName,
              size: blob.size,
              type: mimeType,
            } as CustomFile;

            if (onFilePicked) {
              onFilePicked(customFile, accessToken.value);
            }

            files.push(customFile);
          } catch (error) {
            console.error("Error downloading or uploading file:", error);
          }
        }
      }

      if (onFilesPicked) {
        onFilesPicked(files, accessToken.value);
      }
    } else if (
      data[google.picker.Response.ACTION] == google.picker.Action.CANCEL
    ) {
      if (onFilePicked) {
        onFilePicked(undefined, accessToken.value);
      }
      if (onFilesPicked) {
        onFilesPicked([], accessToken.value);
      }
    }
  };

  const signInToGoogle = async () => {
    const tokenClientState = useState("tokenClient");

    // Request an access token.
    (tokenClientState.value as any).callback = async (response: any) => {
      if (response.error !== undefined) {
        throw response;
      }
      const accessToken = response.access_token;
      openGooglePicker(accessToken);
    };

    if (accessToken === null) {
      // Prompt the user to select a Google Account and ask for consent to share their data
      // when establishing a new session.
      (tokenClientState.value as any).requestAccessToken({ prompt: "consent" });
    } else {
      // Skip display of account chooser and consent dialog for an existing session.
      (tokenClientState.value as any).requestAccessToken({ prompt: "" });
    }
  };

  const getAccessToken = async () => {
    return new Promise((resolve, reject) => {
      const tokenClient = (window as any).tokenClient;

      // Create a separate function for the callback
      const handleTokenResponse = (response: any) => {
        if (response.error !== undefined) {
          reject(response);
          return;
        }
        resolve(response.access_token);
      };

      tokenClient.callback = handleTokenResponse;

      try {
        if (accessToken.value === null) {
          (tokenClient as any).requestAccessToken({
            prompt: "consent",
            callback: handleTokenResponse,
          });
        } else {
          (tokenClient as any).requestAccessToken({
            prompt: "",
            callback: handleTokenResponse,
          });
        }
      } catch (error) {
        reject(error);
      }
    });
  };

  // const getAccessToken = async () => {
  //   return new Promise((resolve, reject) => {
  //     // Request an access token.
  //     (tokenClientState.value as any).callback = async (response: any) => {
  //       if (response.error !== undefined) {
  //         throw response;
  //       }
  //       const accessToken = response.access_token;

  //       resolve(accessToken);
  //     };

  //     if (accessToken === null) {
  //       // Prompt the user to select a Google Account and ask for consent to share their data
  //       // when establishing a new session.
  //       (tokenClientState.value as any).requestAccessToken({
  //         prompt: "consent",
  //       });
  //     } else {
  //       // Skip display of account chooser and consent dialog for an existing session.
  //       (tokenClientState.value as any).requestAccessToken({ prompt: "" });
  //     }
  //   });
  // };

  const pickGoogleDriveFile = async () => {
    signInToGoogle();
  };

  const createFileInFolder = async (
    folderId: string,
    fileMetadata: {
      name: string;
      mimeType: string;
      content?: string | Blob;
    }
  ) => {
    if (!accessToken.value) {
      throw new Error("No access token available");
    }

    const escapeGoogleDriveQueryValue = (value: string) => {
      return value.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
    };

    const splitFileName = (fileName: string) => {
      const lastDotIndex = fileName.lastIndexOf(".");

      if (lastDotIndex <= 0) {
        return {
          baseName: fileName,
          extension: "",
        };
      }

      return {
        baseName: fileName.slice(0, lastDotIndex),
        extension: fileName.slice(lastDotIndex),
      };
    };

    const doesFileNameExistInFolder = async (fileName: string) => {
      const query = [
        `'${folderId}' in parents`,
        "trashed = false",
        `name = '${escapeGoogleDriveQueryValue(fileName)}'`,
      ].join(" and ");

      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(
          query
        )}&fields=files(id,name)&pageSize=1`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken.value}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to check for existing files");
      }

      const data = await response.json();

      return Array.isArray(data.files) && data.files.length > 0;
    };

    const getUniqueFileNameInFolder = async (fileName: string) => {
      const { baseName, extension } = splitFileName(fileName);

      let candidateName = fileName;
      let copyIndex = 1;

      while (await doesFileNameExistInFolder(candidateName)) {
        candidateName = `${baseName} (Copy ${copyIndex})${extension}`;
        copyIndex += 1;
      }

      return candidateName;
    };

    const uniqueFileName = await getUniqueFileNameInFolder(fileMetadata.name);

    // First create the file metadata
    const metadata = {
      name: uniqueFileName,
      mimeType: fileMetadata.mimeType,
      parents: [folderId],
    };

    if (fileMetadata.content) {
      // For binary content (like images, PDFs)
      const form = new FormData();
      const uploadFile =
        typeof fileMetadata.content === "string"
          ? new File([fileMetadata.content], uniqueFileName, {
              type: fileMetadata.mimeType,
            })
          : new File([fileMetadata.content], uniqueFileName, {
              type:
                fileMetadata.content.type ||
                fileMetadata.mimeType ||
                "application/octet-stream",
            });

      form.append(
        "metadata",
        new Blob([JSON.stringify(metadata)], { type: "application/json" })
      );
      form.append("file", uploadFile, uniqueFileName);

      const response = await fetch(
        "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken.value}`,
          },
          body: form,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to create file");
      }

      return await response.json();
    } else {
      // For empty files or Google Workspace documents
      const response = await fetch(
        "https://www.googleapis.com/drive/v3/files",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken.value}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(metadata),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to create file");
      }

      return await response.json();
    }
  };

  const createFolderInFolder = async (folderId: string, folderName: string) => {
    if (!accessToken.value) {
      throw new Error("No access token available");
    }

    const response = await fetch(`https://www.googleapis.com/drive/v3/files`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken.value}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: folderName,
        mimeType: "application/vnd.google-apps.folder",
        parents: [folderId],
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to create folder");
    }

    return await response.json();
  };

  return {
    pickGoogleDriveFile,
    createFileInFolder,
    createFolderInFolder,
    getAccessToken,
  };
};
